/**
 * TutorialContext
 *
 * Manages the first-time user experience (FTUE) tutorial flow.
 * Tracks tutorial progress, handles step advancement, and manages rewards.
 * State is persisted to the backend for per-user storage.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useInventory } from './InventoryContext';
import { apiGetTutorialState, apiUpdateTutorialState, apiResetTutorial } from '../services/apiService';

const TutorialContext = createContext(null);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};

// Tutorial step order
export const TUTORIAL_STEP_ORDER = [
  'welcome',
  'starter-select',
  'career-goal',
  'career-start',
  'inspiration-skip',
  'support-auto',
  'training-intro',
  'battle-intro',
  'battle-select',
  'move-intro',
  'move-learn',
  'rest-intro',
  'gym-intro',
  'gacha-intro',
  'pokemon-gacha',
  'support-gacha',
  'new-career-prompt',
  'inspiration-intro',
  'support-select-intro',
  'tutorial-complete'
];

export const TutorialProvider = ({ children }) => {
  const { authToken } = useAuth();
  const { loadPrimos } = useInventory();

  // Core tutorial state
  const [tutorialActive, setTutorialActive] = useState(false);
  const [tutorialStep, setTutorialStep] = useState('welcome');
  const [tutorialCompleted, setTutorialCompleted] = useState(true); // Default to completed until loaded
  const [freePokemonPullUsed, setFreePokemonPullUsed] = useState(true);
  const [freeSupportPullUsed, setFreeSupportPullUsed] = useState(true);
  const [pokeclockFirstDefeatTutorialShown, setPokeclockFirstDefeatTutorialShown] = useState(false);
  const [pokeclockUsageTutorialShown, setPokeclockUsageTutorialShown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showRewardModal, setShowRewardModal] = useState(false);

  // Track if we've shown specific in-career tutorials (session-only, not persisted)
  const [shownSteps, setShownSteps] = useState(new Set());

  // Debounce ref to prevent rapid API calls
  const updateTimeoutRef = useRef(null);

  // Load tutorial state from backend when authenticated
  useEffect(() => {
    const loadTutorialState = async () => {
      console.log('[Tutorial] loadTutorialState called, authToken:', authToken ? 'present' : 'missing');

      if (!authToken) {
        console.log('[Tutorial] No authToken, skipping API call');
        setIsLoading(false);
        return;
      }

      try {
        console.log('[Tutorial] Loading tutorial state from backend');
        const state = await apiGetTutorialState(authToken);
        console.log('[Tutorial] Loaded state:', state);

        setTutorialCompleted(state.tutorialCompleted);
        setTutorialStep(state.tutorialStep);
        setTutorialActive(state.tutorialActive);
        setFreePokemonPullUsed(state.freePokemonPullUsed);
        setFreeSupportPullUsed(state.freeSupportPullUsed);
        setPokeclockFirstDefeatTutorialShown(state.pokeclockFirstDefeatTutorialShown || false);
        setPokeclockUsageTutorialShown(state.pokeclockUsageTutorialShown || false);
      } catch (error) {
        console.error('[Tutorial] Failed to load tutorial state:', error);
        // On error, assume tutorial completed to not annoy returning users
        setTutorialCompleted(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadTutorialState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]); // Only depend on authToken, not user - we don't want to reload on profile updates

  // Debounced save to backend
  const saveTutorialState = useCallback((state) => {
    if (!authToken) return;

    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Debounce updates by 500ms to batch rapid changes
    updateTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('[Tutorial] Saving tutorial state to backend:', state);
        await apiUpdateTutorialState(state, authToken);
      } catch (error) {
        console.error('[Tutorial] Failed to save tutorial state:', error);
      }
    }, 500);
  }, [authToken]);

  // Start tutorial (for new users or replay)
  const startTutorial = useCallback((isReplay = false) => {
    console.log('[Tutorial] Starting tutorial, isReplay:', isReplay);
    setTutorialActive(true);
    setTutorialStep('welcome');
    setShownSteps(new Set());

    // If replaying, don't reset the free pulls
    if (!isReplay) {
      saveTutorialState({
        tutorialActive: true,
        tutorialStep: 'welcome',
        tutorialCompleted: false
      });
    } else {
      saveTutorialState({
        tutorialActive: true,
        tutorialStep: 'welcome'
      });
    }
  }, [saveTutorialState]);

  // Advance to next step
  const advanceStep = useCallback((specificStep = null) => {
    let nextStep;

    if (specificStep) {
      console.log('[Tutorial] Advancing to specific step:', specificStep);
      nextStep = specificStep;
    } else {
      const currentIndex = TUTORIAL_STEP_ORDER.indexOf(tutorialStep);
      if (currentIndex < TUTORIAL_STEP_ORDER.length - 1) {
        nextStep = TUTORIAL_STEP_ORDER[currentIndex + 1];
        console.log('[Tutorial] Advancing from', tutorialStep, 'to', nextStep);
      }
    }

    if (nextStep) {
      setTutorialStep(nextStep);
      saveTutorialState({ tutorialStep: nextStep });
    }
  }, [tutorialStep, saveTutorialState]);

  // Mark a step as shown (for one-time in-career tutorials - session only)
  const markStepShown = useCallback((stepId) => {
    setShownSteps(prev => new Set([...prev, stepId]));
  }, []);

  // Check if a step has been shown
  const hasShownStep = useCallback((stepId) => {
    return shownSteps.has(stepId);
  }, [shownSteps]);

  // Check if free Pokemon 10-pull is available
  const canUseFreePokemonPull = useCallback(() => {
    return tutorialActive && tutorialStep === 'pokemon-gacha' && !freePokemonPullUsed;
  }, [tutorialActive, tutorialStep, freePokemonPullUsed]);

  // Check if free Support 10-pull is available
  const canUseFreeSupportPull = useCallback(() => {
    return tutorialActive && tutorialStep === 'support-gacha' && !freeSupportPullUsed;
  }, [tutorialActive, tutorialStep, freeSupportPullUsed]);

  // Mark free Pokemon pull as used
  const markFreePokemonPullUsed = useCallback(() => {
    console.log('[Tutorial] Using free Pokemon 10-pull');
    setFreePokemonPullUsed(true);
    saveTutorialState({ freePokemonPullUsed: true });
  }, [saveTutorialState]);

  // Mark free Support pull as used
  const markFreeSupportPullUsed = useCallback(() => {
    console.log('[Tutorial] Using free Support 10-pull');
    setFreeSupportPullUsed(true);
    saveTutorialState({ freeSupportPullUsed: true });
  }, [saveTutorialState]);

  // Complete tutorial
  const completeTutorial = useCallback(async () => {
    if (!authToken) return;

    console.log('[Tutorial] Completing tutorial');
    setTutorialActive(false);
    setTutorialCompleted(true);
    setShowRewardModal(true);

    try {
      // Call backend - it will grant 2000 primos and return updated primo count
      const response = await apiUpdateTutorialState({
        tutorialActive: false,
        tutorialCompleted: true
      }, authToken);

      console.log('[Tutorial] Tutorial completion response:', response);

      // If backend returned primos, update immediately
      if (response.primos !== undefined && loadPrimos) {
        console.log('[Tutorial] Updating primos from backend response:', response.primos);
        await loadPrimos(); // Force refresh to get new primo count
      }
    } catch (error) {
      console.error('[Tutorial] Failed to complete tutorial:', error);
    }
  }, [authToken, loadPrimos]);

  // Reset tutorial (for replay from settings)
  const resetTutorial = useCallback(async () => {
    if (!authToken) return;

    try {
      console.log('[Tutorial] Resetting tutorial via API');
      const result = await apiResetTutorial(authToken);

      setTutorialCompleted(result.tutorialCompleted);
      setTutorialStep(result.tutorialStep);
      setTutorialActive(result.tutorialActive);
      setFreePokemonPullUsed(result.freePokemonPullUsed);
      setFreeSupportPullUsed(result.freeSupportPullUsed);
      setShownSteps(new Set());

      return true;
    } catch (error) {
      console.error('[Tutorial] Failed to reset tutorial:', error);
      return false;
    }
  }, [authToken]);

  // Check if currently on a specific step
  const isTutorialStep = useCallback((stepId) => {
    return tutorialActive && tutorialStep === stepId;
  }, [tutorialActive, tutorialStep]);

  // Check if this is the first tutorial playthrough (for free pulls)
  const isFirstTutorial = useCallback(() => {
    return !freePokemonPullUsed || !freeSupportPullUsed;
  }, [freePokemonPullUsed, freeSupportPullUsed]);

  // Check if tutorial should auto-start for new user
  const shouldStartTutorial = useCallback((pokemonInventory) => {
    // Start tutorial if:
    // 1. User has no Pokemon (brand new)
    // 2. Tutorial hasn't been completed
    // 3. Tutorial isn't already active
    // 4. We've finished loading from backend
    return !isLoading && pokemonInventory.length === 0 && !tutorialCompleted && !tutorialActive;
  }, [tutorialCompleted, tutorialActive, isLoading]);

  // Get current step index (for progress display)
  const getCurrentStepIndex = useCallback(() => {
    return TUTORIAL_STEP_ORDER.indexOf(tutorialStep);
  }, [tutorialStep]);

  // Get total step count
  const getTotalSteps = useCallback(() => {
    return TUTORIAL_STEP_ORDER.length;
  }, []);

  // Check if pokeclock first defeat tutorial should show
  const shouldShowPokeclockFirstDefeat = useCallback(() => {
    return !pokeclockFirstDefeatTutorialShown;
  }, [pokeclockFirstDefeatTutorialShown]);

  // Check if pokeclock usage tutorial should show
  const shouldShowPokeclockUsage = useCallback(() => {
    return !pokeclockUsageTutorialShown;
  }, [pokeclockUsageTutorialShown]);

  // Mark pokeclock first defeat tutorial as shown
  const markPokeclockFirstDefeatShown = useCallback(() => {
    console.log('[Tutorial] Marking pokeclock first defeat tutorial as shown');
    setPokeclockFirstDefeatTutorialShown(true);
    saveTutorialState({ pokeclockFirstDefeatTutorialShown: true });
  }, [saveTutorialState]);

  // Mark pokeclock usage tutorial as shown
  const markPokeclockUsageShown = useCallback(() => {
    console.log('[Tutorial] Marking pokeclock usage tutorial as shown');
    setPokeclockUsageTutorialShown(true);
    saveTutorialState({ pokeclockUsageTutorialShown: true });
  }, [saveTutorialState]);

  const value = {
    // State
    tutorialActive,
    tutorialStep,
    tutorialCompleted,
    freePokemonPullUsed,
    freeSupportPullUsed,
    isLoading,
    showRewardModal,
    setShowRewardModal,

    // Methods
    startTutorial,
    advanceStep,
    completeTutorial,
    resetTutorial,
    isTutorialStep,
    isFirstTutorial,
    shouldStartTutorial,
    canUseFreePokemonPull,
    canUseFreeSupportPull,
    markFreePokemonPullUsed,
    markFreeSupportPullUsed,
    markStepShown,
    hasShownStep,
    getCurrentStepIndex,
    getTotalSteps,
    shouldShowPokeclockFirstDefeat,
    shouldShowPokeclockUsage,
    markPokeclockFirstDefeatShown,
    markPokeclockUsageShown,

    // Constants
    TUTORIAL_STEP_ORDER
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
